import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { handleApiError } from '../utils/errorHandler';
import { GroupResponse, GroupWithFacilitiesResponse } from '../types/api/group.types';
import { AdminUserListItem, AdminUserDetail } from '../types/api/admin.types';
import { getBackendUrl } from '../utils/apiUrl';
import { browserLogger } from '../utils/browserLogger';
import { getAnonymousUserId, getSessionId } from '../utils/anonymousUser';

// Extend Window interface for TypeScript
declare global {
  interface Window {
    REACT_APP_RXROOSTER_BACKEND_URL?: string;
  }
}

// Client configuration: Support runtime override from mobile WebView
// CRA replaces process.env.* at build time, but we check window first for mobile
const BASE_URL = getBackendUrl();
// Debug (can remove after verification)
// eslint-disable-next-line no-console
console.log('TS API BASE_URL', BASE_URL);

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Endpoints that a non-approved GROUP user must never call. The backend enforces
// this with GroupApprovedGuard (403 + GROUP_NOT_APPROVED), but blocking client-side
// means non-approved groups don't flood the network tab with futile requests while
// they're parked on /company/settings.
// Match method+pathPrefix so we don't accidentally block reads the settings page needs.
const GROUP_RESTRICTED_MATCHERS: Array<(method: string, url: string) => boolean> = [
  (m, u) => m === 'GET' && u.startsWith('/listings') && !u.startsWith('/listings/anonymous'),
  (m, u) => m === 'GET' && u.startsWith('/facilities/') && u.includes('/listings'),
  (m, u) => m === 'GET' && u === '/facilities/listings',
  (m, u) => m === 'GET' && u.startsWith('/matching/'),
  // Mutations to facility/listing endpoints are also gated server-side; blocking
  // them here prevents the UI from firing writes the server will reject anyway.
  (m, u) => m !== 'GET' && u.startsWith('/listings'),
  (m, u) => m !== 'GET' && /^\/facilities\/[^/]+\/listings/.test(u),
];

const readGroupApprovalCache = (): { blocked: boolean; payload: any } => {
  try {
    const raw = localStorage.getItem('groupApprovalCache');
    if (!raw) return { blocked: false, payload: null };
    const parsed = JSON.parse(raw);
    if (parsed.role !== 'GROUP') return { blocked: false, payload: null };
    const status = parsed.reviewStatus;
    if (!status || status === 'approved') return { blocked: false, payload: null };
    return { blocked: true, payload: parsed };
  } catch {
    return { blocked: false, payload: null };
  }
};

// Add request interceptor to inject auth token and gate GROUP-restricted calls
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Attach visitor-analytics headers so the backend VisitorCaptureInterceptor
    // can attribute listing_view / contact_reveal events. Best-effort only —
    // never block a request on analytics headers.
    try {
      const aid = getAnonymousUserId();
      const sid = getSessionId();
      if (aid) config.headers['X-Anonymous-Id'] = aid;
      if (sid) config.headers['X-Session-Id'] = sid;
    } catch (e) {
      // ignore
    }

    // Short-circuit restricted calls for non-approved groups so we don't even
    // hit the network — the server would 403 anyway.
    const { blocked, payload } = readGroupApprovalCache();
    if (blocked) {
      const method = (config.method || 'GET').toUpperCase();
      const url = config.url || '';
      const isRestricted = GROUP_RESTRICTED_MATCHERS.some((fn) => fn(method, url));
      if (isRestricted) {
        try {
          window.dispatchEvent(
            new CustomEvent('showUnderReviewModal', {
              detail: {
                message: 'This action is not available while your account is under review.',
                reviewStatus: payload.reviewStatus,
                reviewReason: payload.reviewReason || '',
                reviewNotes: payload.reviewNotes || '',
                needsInfoFields: payload.needsInfoFields || [],
              },
            }),
          );
        } catch (e) { /* ignore */ }
        const err: any = new Error('GROUP_NOT_APPROVED');
        err.code = 'GROUP_NOT_APPROVED';
        err.isGroupApprovalBlock = true;
        return Promise.reject(err);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Only logout if it's actually an auth token issue (not role/permission)
      const rawMessage = (error.response?.data as any)?.message;
      const msg = typeof rawMessage === 'string' ? rawMessage.toLowerCase() : '';
      const tokenMissing = !localStorage.getItem('authToken');
      const isTokenError = tokenMissing || msg.includes('token') || msg.includes('unauthorized') || msg.includes('expired');

      if (isTokenError) {
        // eslint-disable-next-line no-console
        console.log('Token authentication failed, logging out...');
        localStorage.removeItem('authToken');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      } else {
        // eslint-disable-next-line no-console
        console.warn('Permission denied (401) but token is valid - role/permission issue');
      }
      // Otherwise, let the error propagate to be handled by the component
    }

    // Handle forbidden errors (typically role-based access control)
    if (error.response?.status === 403) {
      console.warn('Access forbidden (403) - insufficient permissions');

      // Group-review-status gate: when the backend blocks a group action
      // because the account is not APPROVED, surface the UnderReviewModal.
      // The backend sets code='GROUP_NOT_APPROVED' and returns the reviewStatus,
      // reviewReason, reviewNotes, and needsInfoFields for the frontend to use.
      const body: any = error.response?.data;
      const payload = body?.message && typeof body.message === 'object' ? body.message : body;
      if (payload?.code === 'GROUP_NOT_APPROVED') {
        try {
          window.dispatchEvent(
            new CustomEvent('showUnderReviewModal', {
              detail: {
                message: payload.message || 'This action is not available for your account right now.',
                reviewStatus: payload.reviewStatus || 'pending',
                reviewReason: payload.reviewReason || '',
                reviewNotes: payload.reviewNotes || '',
                needsInfoFields: payload.needsInfoFields || [],
              },
            }),
          );
        } catch (e) {
          // No-op: modal not mounted yet, fall through to normal error handling.
        }
      }
      // Don't logout, just let the component handle it
    }

    return Promise.reject(error);
  }
);

// Capture x-trace-id from every response and surface failed requests to the
// browserLogger so debug mode picks them up.
axiosInstance.interceptors.response.use(
  (response) => {
    const traceId = response.headers?.['x-trace-id'];
    if (traceId) browserLogger.setTraceId(traceId);
    return response;
  },
  (error: AxiosError) => {
    const traceId = (error.response?.headers as any)?.['x-trace-id'];
    if (traceId) browserLogger.setTraceId(traceId);
    browserLogger.error('api.request.failed', {
      url: error.config?.url,
      status: error.response?.status,
      method: error.config?.method,
    });
    return Promise.reject(error);
  }
);

// Typed API function creator
const sanitizeParams = (params?: Record<string, any>) => {
  if (!params) return undefined;

  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const createApiFunction = <TResponse, TRequest = void>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  requiresAuth: boolean = true
) => {
  return async (data?: TRequest, params?: any): Promise<TResponse> => {
    try {
      // Check for auth token if required
      if (requiresAuth && !localStorage.getItem('authToken')) {
        throw new Error('Authentication required');
      }

      // Prepare request config
      const config: AxiosRequestConfig = {
        method,
        url,
        params: sanitizeParams(params),
      };

      // Add request body for non-GET requests
      if (method !== 'GET' && data) {
        config.data = data;
      }

      // Execute request
      const response: AxiosResponse<TResponse> = await axiosInstance(config);
      return response.data;
    } catch (error) {
      throw handleApiError(error, `API Error (${method} ${url})`);
    }
  };
};

// Type for pagination parameters - will be used in future list endpoints
// Currently not used but kept for upcoming pagination implementation
/**
 * @todo Use this interface for all paginated endpoints
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface PaginationParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface AdminProviderDirectoryQuery {
  journey?: 'profile' | 'services' | 'tour' | 'done';
  services?: 'live' | 'inactive' | 'none';
  providerType?: string;
  hasMatches?: 'yes' | 'no';
  matchType?: 'internal' | 'external';
  compensationType?: 'hourly' | 'salary' | 'both' | 'none';
  state?: string;
  sortField?: 'hourly' | 'salary' | 'signUpDate' | 'lastActivity' | 'matchCount' | 'uniqueEmployerCount';
  sortDirection?: 'asc' | 'desc';
}

// API endpoints
export const api = {
  auth: {
    // Auth endpoints
    login: createApiFunction<{ token: string }, { email: string; password: string }>(
      'POST',
      '/auth/login',
      false
    ),
    me: createApiFunction<any>('GET', '/auth/me'),
  },
  
  groups: {
    // Group endpoints
    getById: (id: string) => {
      return createApiFunction<GroupWithFacilitiesResponse, void>(
        'GET',
        `/groups/${id}`,
        true
      )();
    },
    
    update: (id: string, data: Partial<GroupResponse>) => {
      return createApiFunction<GroupResponse, Partial<GroupResponse>>(
        'PUT',
        `/groups/${id}`,
        true
      )(data);
    },

    updateField: (id: string, field: string, value: any) => {
      return createApiFunction<GroupResponse, Record<string, any>>(
        'PATCH',
        `/groups/${id}`,
        true
      )({ [field]: value });
    },

    updateReviewStatus: (id: string, payload: {
      underReview?: boolean;
      reviewStatus?: string;
      reviewNotes?: string;
      reviewReason?: string;
      // Mandatory server-side on reject and needs_info (D1.2.5): the code, not
      // the free text, is what the employer's remediation sentence is keyed off.
      reasonCode?: string;
      needsInfoFields?: string[];
    }) => {
      return createApiFunction<GroupResponse, typeof payload>(
        'PATCH',
        `/groups/${id}/review-status`,
        true
      )(payload);
    },

    submitIntake: createApiFunction<any, any>(
      'POST',
      '/groups/intake',
      true
    ),
    
    updateIntake: (id: string, data: any) => {
      return createApiFunction<any, any>(
        'PUT',
        `/groups/${id}/intake`,
        true
      )(data);
    },
  },
  
  facilities: {
    // Facility endpoints
    getByGroupId: (groupId: string) => {
      return createApiFunction<any[], void>(
        'GET',
        `/groups/${groupId}/facilities`,
        true
      )();
    },
    
    addToGroup: (groupId: string, facilityData: any) => {
      return createApiFunction<any, any>(
        'POST',
        `/groups/${groupId}/facilities`,
        true
      )(facilityData);
    },
    
    update: (id: string, data: any) => {
      return createApiFunction<any, any>(
        'PATCH',
        `/facilities/${id}`,
        true
      )(data);
    },
    
    removeFromGroup: (groupId: string, facilityId: string) => {
      return createApiFunction<void, void>(
        'DELETE',
        `/groups/${groupId}/facilities/${facilityId}`,
        true
      )();
    },
  },
  
  listings: {
    // Listing endpoints
    getByFacilityId: (facilityId: string) => {
      return createApiFunction<any[], void>(
        'GET',
        `/facilities/${facilityId}/listings`,
        true
      )();
    },
    
    add: (facilityId: string, listingData: any) => {
      return createApiFunction<any, any>(
        'POST',
        `/facilities/${facilityId}/listings`,
        true
      )(listingData);
    },
    
    update: (facilityId: string, listingId: string, data: any) => {
      return createApiFunction<any, any>(
        'PATCH',
        `/facilities/${facilityId}/listings/${listingId}`,
        true
      )(data);
    },
    
    remove: (facilityId: string, listingId: string) => {
      return createApiFunction<void, void>(
        'DELETE',
        `/facilities/${facilityId}/listings/${listingId}`,
        true
      )();
    },
  },

  admin: {
    users: {
      list: (role?: string) =>
        createApiFunction<AdminUserListItem[], void>(
          'GET',
          '/admin/users',
          true
        )(undefined, role ? { role } : undefined),
      getById: (id: string) =>
        createApiFunction<AdminUserDetail, void>(
          'GET',
          `/admin/users/${id}`,
          true
        )(),
    },
    groups: {
      list: (search?: string, page?: number, limit?: number) =>
        createApiFunction<any, void>(
          'GET',
          '/admin/groups',
          true
        )(undefined, { search, page, limit }),
      getById: (id: string) =>
        createApiFunction<any, void>(
          'GET',
          `/admin/groups/${id}`,
          true
        )(),
    },
    providerProfiles: {
      list: (
        search?: string,
        page?: number,
        limit?: number,
        directoryQuery?: AdminProviderDirectoryQuery
      ) =>
        createApiFunction<any, void>(
          'GET',
          '/admin/provider-profiles',
          true
        )(undefined, { search, page, limit, ...directoryQuery }),
      getById: (id: string) =>
        createApiFunction<any, void>(
          'GET',
          `/admin/provider-profiles/${id}`,
          true
        )(),
    },
  }
};

export default api;
